import React from 'react';
import { isKeyHotkey } from 'is-hotkey';
import { getEventRange, getEventTransfer } from 'slate-react';
import { Value } from 'slate';
import Html from 'slate-html-serializer';
import { camelCase } from 'lodash';
import { Map } from 'immutable';

import ImageNode from '../components/Image';
import AlignmentNode from '../components/Alignment';

const protocolAndDomainRE = /^(?:\w+:)?\/\/(\S+)$/;
const localhostDomainRE = /^localhost[:?\d]*(?:[^:?\d]\S*)?$/;
const nonLocalhostDomainRE = /^[^\s.]+.\S{2,}$/;

/**
 * Default image height when inserted from url, drag/drop, copy/paste
 */
const DEFAULT_IMAGE_HEIGHT = 100;
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_FONT_FAMILY = 'Arial';
const DEFAULT_PAGE_SIZE = 'letter';

const DEFAULT_VALUE = '<p></p>';

const BLOCK_TAGS = {
  blockquote: 'quote',
  p: 'paragraph',
  pre: 'code',
  img: 'image',
  div: 'div',
};

const MARK_TAGS = {
  em: 'italic',
  strong: 'bold',
  u: 'underline',
  span: 'span', // font-family, font-size
};

const SUPPORTED_FONT_SIZES = [
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  14,
  16,
  18,
  20,
  22,
  24,
  26,
  28,
  30,
  36,
  48,
  60,
  72,
  96,
];

const SUPPORTED_FONT_FAMILIES = [
  'Times',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Arial',
  'Helvetica',
  'Courier',
  'Lato',
];

const SUPPORTED_MARGINS = [0.5, 0.75, 1, 1.25, 2];

const SUPPORTED_PAGE_SIZES = ['letter', 'legal'];

/**
 * Defines how the HTML is parsed into slate
 * Defines how slate is serialized into HTML
 */
const rules = [
  {
    deserialize(el, next) {
      const type = BLOCK_TAGS[el.tagName.toLowerCase()];
      if (type) {
        switch (type) {
          case 'div': // only used for alignment
            return {
              object: 'block',
              type: 'alignment',
              data: {
                alignment: el.style['text-align'],
              },
              nodes: next(el.childNodes),
            };
          case 'image':
            return {
              object: 'inline',
              type: type,
              data: {
                src: el.getAttribute('src'),
                width: el.getAttribute('width'),
                height: el.getAttribute('height'),
              },
              nodes: next(el.childNodes),
            };
          default:
            return {
              object: 'block',
              type: type,
              data: {
                className: el.getAttribute('class'),
              },
              nodes: next(el.childNodes),
            };
        }
      }
    },
    serialize(obj, children) {
      if (obj.object === 'inline') {
        switch (obj.type) {
          case 'image':
            return (
              <img
                alt={obj.data.get('alt') || ''}
                width={obj.data.get('width')}
                height={obj.data.get('height')}
                src={obj.data.get('src')}
              />
            );
          default:
        }
      }
      if (obj.object === 'block') {
        switch (obj.type) {
          case 'alignment':
            return (
              <div style={{ textAlign: obj.data.get('alignment') }}>
                {children}
              </div>
            );
          case 'paragraph':
            return (
              <p
                style={{
                  fontSize: DEFAULT_FONT_SIZE,
                  fontFamily: DEFAULT_FONT_FAMILY,
                }}
                className={obj.data.get('className')}
              >
                {children}
              </p>
            );
          default:
        }
      }
    },
  },
  {
    deserialize(el, next) {
      const type = MARK_TAGS[el.tagName.toLowerCase()];
      if (type) {
        switch (type) {
          case 'span': // reused span for font-family and font-size
            const markType = el.getAttribute('data-mark');
            const markValue = el.style[markType];
            return {
              object: 'mark',
              type: markType,
              data: {
                [markType]: markValue,
              },
              nodes: next(el.childNodes),
            };
          case 'underline':
            return {
              object: 'mark',
              type: 'underlined',
              nodes: next(el.childNodes),
            };
          default:
            return {
              object: 'mark',
              type: type,
              nodes: next(el.childNodes),
            };
        }
      }
    },
    serialize(obj, children) {
      if (obj.object === 'mark') {
        switch (obj.type) {
          case 'font-size':
          case 'font-family':
            const style = {
              [camelCase(obj.type)]: obj.data.get(obj.type),
            };
            return (
              <span data-mark={obj.type} style={style}>
                {children}
              </span>
            );
          case 'bold':
            return <strong>{children}</strong>;
          case 'italic':
            return <em>{children}</em>;
          case 'underlined':
            return <u>{children}</u>;
          default:
        }
      }
    },
  },
];

/**
 * HTML serializer/deserializer
 */
const html = new Html({ rules });

const createValue = v => {
  let value = v || DEFAULT_VALUE;
  if (!Value.isValue(value)) {
    switch (typeof value) {
      case 'string':
        value = html.deserialize(value);
        break;
      default:
        value = html.deserialize(DEFAULT_VALUE);
        break;
    }
  }
  return value;
};

const renderMark = (props, next) => {
  const { children, mark, attributes } = props;

  switch (mark.type) {
    case 'font-size':
    case 'font-family':
      const style = {
        [camelCase(mark.type)]: mark.data.get(mark.type),
      };
      return (
        <span style={style} {...attributes}>
          {children}
        </span>
      );
    case 'bold':
      return <strong {...attributes}>{children}</strong>;
    case 'italic':
      return <em {...attributes}>{children}</em>;
    case 'underlined':
      return <u {...attributes}>{children}</u>;
    default:
      return next();
  }
};

const renderNode = (props, next) => {
  const { attributes, node, isFocused, children, editor } = props;

  switch (node.type) {
    case 'alignment':
      const alignment = node.data.get('alignment');
      return <AlignmentNode alignment={alignment}>{children}</AlignmentNode>;
    case 'image':
      const src = node.data.get('src');
      const width = node.data.get('width');
      const height = node.data.get('height');
      return (
        <ImageNode
          editor={editor}
          src={src}
          width={width}
          height={height}
          selected={isFocused}
          {...attributes}
        />
      );
    default:
      return next();
  }
};

const getOS = () => {
  let userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux';
  }
  return os;
};

/**
 *
 * @param {String} string - Checks if the string is a valid URL
 */
const isUrl = string => {
  if (typeof string !== 'string') {
    return false;
  }

  const match = string.match(protocolAndDomainRE);
  if (!match) {
    return false;
  }

  const everythingAfterProtocol = match[1];
  if (!everythingAfterProtocol) {
    return false;
  }

  if (
    localhostDomainRE.test(everythingAfterProtocol) ||
    nonLocalhostDomainRE.test(everythingAfterProtocol)
  ) {
    return true;
  }

  return false;
};

const SUPPORTED_IMAGE_TYPES = ['jpeg', 'jpg', 'png'];
const IMAGE_REGEX = new RegExp(`.(${SUPPORTED_IMAGE_TYPES.join('|')})$`);

const isImage = uri => typeof uri === 'string' && IMAGE_REGEX.test(uri);
const isImageType = uri => typeof uri === 'string' && SUPPORTED_IMAGE_TYPES.includes(uri)
/**
 * Check if the current selection has a mark with `type` in it.
 *
 * @param {String} type
 * @return {Boolean}
 */
const hasMark = (value, type) => {
  return value.marks.some(mark => mark.type === type);
};

const hasBlock = (value, type) => {
  return value.blocks.some(node => node.type === type);
};

const hasBlockWithData = (value, type, data) => {
  return value.blocks
    .filter(node => node.type === type)
    .some(
      node => JSON.stringify(node.get('data').toJS()) === JSON.stringify(data)
    );
};

const getMarks = (value, type) =>
  value.marks.filter(mark => mark.type === type);

const getFirstMark = (value, type) => getMarks(value, type).first();

const removeMarkFromSelection = (change, markType) => {
  const hasMarkType = hasMark(change.value, markType);
  if (hasMarkType) {
    const marks = getMarks(change.value, markType);
    marks.map(mark => change.removeMark(mark));
  }
};

/**
 * Define hotkey matchers.
 */
const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');
const isLeftAlignHotkey = isKeyHotkey('mod+shift+l');
const isRightAlignHotkey = isKeyHotkey('mod+shift+r');
const isCenterAlignHotkey = isKeyHotkey('mod+shift+e');
const isJustifyAlignHotkey = isKeyHotkey('mod+shift+j');
const isEnterKey = isKeyHotkey('enter')
/**
 * On key down, check for formatting commands
 *
 * @param {Event} event
 * @param {Change} change
 * @return {Change}
 */
const onKeyDown = (event, change, next) => {
  if (isEnterKey(event)) {
    return change.insertText('\n');
  }
  let mark = '';
  if (isBoldHotkey(event)) {
    mark = 'bold';
  } else if (isItalicHotkey(event)) {
    mark = 'italic';
  } else if (isUnderlinedHotkey(event)) {
    mark = 'underlined';
  } else if (isLeftAlignHotkey(event)) {
    change.setBlocks({
      type: 'alignment',
      data: { alignment: 'left' },
    });
  } else if (isRightAlignHotkey(event)) {
    change.setBlocks({
      type: 'alignment',
      data: { alignment: 'right' },
    });
  } else if (isCenterAlignHotkey(event)) {
    change.setBlocks({
      type: 'alignment',
      data: { alignment: 'center' },
    });
  } else if (isJustifyAlignHotkey(event)) {
    change.setBlocks({
      type: 'alignment',
      data: { alignment: 'justify' },
    });
  } else {
    return next();
  }
  event.preventDefault();
  change.toggleMark(mark);
};

/**
 * Retrieves the modifier key for triggering a command
 * Mac: ⌘
 * Others: Ctrl
 */
const getModKey = () => (getOS() === 'Mac OS' ? '⌘' : 'Ctrl');

const mergeBlocksData = (change, { data }) => {
  const { value } = change;
  const blocks = value.blocks;
  if (blocks) {
    blocks.forEach(type => {
      const mapData = Map(data);
      const originalDataAttr = type.get('data') || Map({});
      const newData = originalDataAttr.merge(mapData);
      const newType = type.set('data', newData);
      change.setBlocks(newType);
    });
  }
  return change;
};

const mergeData = (object, change, { data }) => {
  const mapData = Map(data);
  const originalDataAttr = object.get('data') || Map({});
  const newData = originalDataAttr.merge(mapData);
  const newType = object.set('data', newData);
  return change.setInlines(newType);
};

/**
 * Retrieves the height, width from a remote image or file
 * @param {String} url - URL/URI
 */
const getImageDimensionsFromUrl = url =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', function() {
      resolve({ width: this.naturalWidth, height: this.naturalHeight });
    });
    img.src = url;
  });

/**
 * Inserts an image onto the Editor
 */
const insertImage = (change, { src, width, height }, target) => {
  if (target) {
    change.select(target);
  }

  if (!parseInt(width, 10)) {
    width = 100;
  }

  if (!parseInt(height, 10)) {
    height = 100;
  }

  change.insertInline({
    isVoid: true,
    type: 'image',
    data: { src, width, height },
  });
  return change;
};

/**
 * Handles dropping a file onto the editor
 * Handles copy and paste a file in the editor
 */
const onDropOrPaste = (event, change, next) => {
  const { editor } = change;
  const target = getEventRange(event, editor);
  if (!target && event.type === 'drop') return next();

  const transfer = getEventTransfer(event);
  const { type, text, files } = transfer;

  if (type === 'files') {
    for (const file of files) {
      const reader = new FileReader();
      const [mime] = file.type.split('/');
      if (mime !== 'image') continue;

      reader.addEventListener('load', () => {
        getImageDimensionsFromUrl(reader.result).then(dimensions => {
          const height = DEFAULT_IMAGE_HEIGHT;
          const width = (height / dimensions.height) * dimensions.width;
          editor.change(c => {
            c.call(insertImage, { src: reader.result, height, width }, target);
          });
        });
      });
      reader.readAsDataURL(file);
    }
    return;
  }

  if (type === 'text') {
    if (!isUrl(text)) return next();
    if (!isImage(text)) return next();
    /**
     * Pasting URLs will add as image node
     */
    getImageDimensionsFromUrl(text).then(dimensions => {
      const height = DEFAULT_IMAGE_HEIGHT;
      const width = (height / dimensions.height) * dimensions.width;
      change.call(insertImage, { src: text, width, height });
    });
    return;
  }
  next();
};

const PX_PT_RATIO = 72 / 96;

const pxToPt = (px = 0) => px * PX_PT_RATIO;

const ptToPx = (pt = 0) => pt / PX_PT_RATIO;

const inchesToPx = (inches = 0) => inches * 96;

const pxToInches = (pixels = 0) => pixels / 96;

/**
 * Generates the height, width, and margins
 * @param {String} type
 * @param {Object} margins
 * @param {Number} margins.top
 * @param {Number} margins.right
 * @param {Number} margins.bottom
 * @param {Number} margins.left
 *
 */
const getDimensions = (type, margins = {}) => {
  const { top = 1, right = 1, bottom = 1, left = 1 } = margins;
  let height = inchesToPx(11);
  let width = inchesToPx(8.5);
  switch (type) {
    case 'legal':
      height = inchesToPx(14);
      width = inchesToPx(8.5);
      break;
    case 'letter':
    default:
      height = inchesToPx(11);
      width = inchesToPx(8.5);
      break;
  }
  return {
    height,
    width,
    paddingTop: inchesToPx(top),
    paddingRight: inchesToPx(right),
    paddingBottom: inchesToPx(bottom),
    paddingLeft: inchesToPx(left),
  };
};

export {
  DEFAULT_PAGE_SIZE,
  DEFAULT_FONT_SIZE,
  DEFAULT_FONT_FAMILY,
  DEFAULT_IMAGE_HEIGHT,
  SUPPORTED_FONT_SIZES,
  SUPPORTED_FONT_FAMILIES,
  SUPPORTED_MARGINS,
  SUPPORTED_PAGE_SIZES,
  html,
  createValue,
  renderMark,
  renderNode,
  getOS,
  getModKey,
  isUrl,
  isImage,
  isImageType,
  getFirstMark,
  getMarks,
  hasMark,
  removeMarkFromSelection,
  hasBlock,
  onKeyDown,
  onDropOrPaste,
  insertImage,
  getDimensions,
  inchesToPx,
  pxToInches,
  pxToPt,
  ptToPx,
  mergeBlocksData,
  hasBlockWithData,
  mergeData,
  getImageDimensionsFromUrl,
};



// WEBPACK FOOTER //
// ./src/pages/lettersetup/helpers/Editor.js